import { useContext } from "react";
import { ReportInvoiceGroupResponse } from "../../entities/reports/reportInvoiceGroup/ReportInvoiceGroupResponse";
import CompanyService from "../../services/CompanyService";
import FilterService, {
  FilterCollection,
  FilterEntity
} from "../../services/FilterService";
import { formatCurrency, formatNumber } from "../../utils/FormatUtils";
import { sumList } from "../../utils/Utils";
import AdvancedFilters, {
  AdvancedFiltersButton,
  TYPE_REPORT
} from "../shared/components/AdvancedFilters";
import Dropdown from "../shared/components/Dropdown";
import { TranslationService } from "../../services/TranslationService";
import Table, { TableHeader } from "../shared/Table";
import TableContext from "../task/TableContext";
import ErrorMessage from "../shared/components/Error";
import AdvancedFilterService, {
  FilterLine
} from "../../services/AdvancedFilterService";
import { useHistory } from "react-router-dom";
import { FixToTop } from "../shared/FixToTop";
import ReportClientGroupProvider, {
  ReportClientGroupTableContextValues
} from "./providers/ReportClientGroupProvider";
import { parseIntOrDefault } from "../../utils/ParseUtils";
import TooltipComponent from "../shared/TooltipComponent";

const ADDITIONAL_PREFIX = "additional-";
const ReportClientGroup = () => {
  return (
    <ReportClientGroupProvider>
      <ReportClientGroupContainer />
    </ReportClientGroupProvider>
  );
};
const ReportClientGroupContainer = () => {
  const history = useHistory();
  const { error, reload, response, request, setRequest } = useContext(
    TableContext
  ) as ReportClientGroupTableContextValues;

  const headers: TableHeader[] = [
    new TableHeader(
      "value",
      TranslationService.translate.Value,
      false,
      false,
      " text-end "
    ),
    new TableHeader(
      "clients",
      TranslationService.translate.Clients,
      true,
      false,
      " text-end "
    ),
    new TableHeader(
      "pendingAmount",
      TranslationService.translate.Amount,
      true,
      false,
      " text-end "
    ),
    new TableHeader("percentage", "", true, false, " text-end  "),
    new TableHeader("duegraph", "", true, false)
  ];

  const onFilterGroupChange = (value?: string) => {
    setRequest({
      ...request,
      id: parseIntOrDefault(value?.replace(ADDITIONAL_PREFIX, ""))
    });
  };

  const sendToInvoicesWithFilter = (
    dataValues: ReportInvoiceGroupResponse.Item
  ) => {
    const filterOption = FilterService.GetFiltersForEntity(FilterEntity.Client);
    const definition = filterOption.definitions.find(
      (x) => x.Field === ADDITIONAL_PREFIX + request.id
    )!;
    const filters = AdvancedFilterService.parseFilters(request.filter, [
      filterOption
    ]);
    const selectedFilter = new FilterLine();
    selectedFilter.definition = definition;
    selectedFilter.kind = definition.Type;
    selectedFilter.value = dataValues.valueid?.toString() ?? dataValues.value!;
    selectedFilter.operator = 0;
    filters.push(selectedFilter);
    const filterAux = AdvancedFilterService.filtersToRequestString(filters);
    history.push(
      `/${TranslationService.currentLanguage}/client?filter=${filterAux}`
    );
  };

  const groupByItems = FilterService.GetFiltersForPage(
    FilterCollection.OnlyClient
  )[0]
    .definitions.filter(
      (x) => x.Field.startsWith(ADDITIONAL_PREFIX) || x.Field === "-1015"
    )
    .map((x) => ({ text: x.Name, value: x.Field }));

  if (error) {
    return <ErrorMessage onRefresh={reload} />;
  }

  return (
    <div className="container-fluid padding">
      <div className="card mh-100 p-0">
        <FixToTop>
          <h2>{TranslationService.translate.ClientSummary}</h2>
          <div className="genericHeader">
            <div className="searcherFilterHeader">
              <AdvancedFiltersButton savedFiltersId={TYPE_REPORT["Person"]} />
              <div className="d-flex align-items-center">
                <p className="text-granite-gray me-3">
                  {TranslationService.translate.GroupInvoicesBy.replace(
                    TranslationService.translate.Invoices.toLowerCase(),
                    TranslationService.translate.Clients.toLowerCase()
                  )}
                  :
                </p>
                <Dropdown
                  quickFilter
                  optionLabel={TranslationService.translate.Select}
                  onChange={onFilterGroupChange}
                  items={groupByItems}
                />
              </div>
            </div>
          </div>
          <AdvancedFilters page={FilterCollection.OnlyClient} />
        </FixToTop>
        <Table
          headers={headers}
          stickyHeader={true}
          fillHeight={true}
          item={(data) => (
            <ReportInvoiceGroupItem
              dataValues={data.data}
              sendToInvoicesWithFilter={sendToInvoicesWithFilter}
            />
          )}
        >
          <ReportInvoiceGroupTotal response={response} />
        </Table>
      </div>
    </div>
  );
};

const ReportInvoiceGroupItem = ({
  dataValues,
  sendToInvoicesWithFilter
}: {
  dataValues: ReportInvoiceGroupResponse.Item;
  sendToInvoicesWithFilter: (
    dataValues: ReportInvoiceGroupResponse.Item
  ) => void;
}) => {
  const total = useContext(TableContext).response.total || 0.000001;
  const noDueShow = dataValues.amount - dataValues.due;
  const dueCalc = (dataValues.due * 100) / dataValues.max;
  const noDueCalc = (noDueShow * 100) / dataValues.max;

  return (
    <tr onClick={() => sendToInvoicesWithFilter(dataValues)}>
      <td className="text-end ">{dataValues.value}</td>
      <td className="text-end ">{dataValues.cant}</td>
      <td className="text-end ">
        {formatCurrency(
          dataValues.amount,
          CompanyService.getDefaultCurrencyId()
        )}
      </td>
      <td className="text-end ">
        {formatNumber((dataValues.amount / total) * 80)}%
      </td>
      <td>
        <TooltipComponent
          title={
            <div className="popover__wrapper">
              <p>
                {TranslationService.translate.Due}:{" "}
                {formatCurrency(
                  dataValues.due,
                  CompanyService.getDefaultCurrencyId()
                )}
              </p>
              <p>
                {TranslationService.translate.NoDue}:{" "}
                {formatCurrency(
                  noDueShow,
                  CompanyService.getDefaultCurrencyId()
                )}
              </p>
            </div>
          }
        >
          <div
            className="progress progress-amount"
            style={{ marginBottom: "12px" }}
          >
            <div
              className="progress-bar bg-danger bg-gradient bg-opacity-75"
              style={{ width: dueCalc + "%" }}
            />
            <div
              className="progress-bar bg-primary bg-gradient progress-bar-stripped bg-opacity-75"
              style={{ width: noDueCalc + "%" }}
            />
          </div>
        </TooltipComponent>
      </td>
    </tr>
  );
};

const ReportInvoiceGroupTotal = ({
  response
}: {
  response: ReportInvoiceGroupResponse;
}) => {
  const { translate } = TranslationService;
  if ((response?.list.length ?? 0) === 0) {
    return (
      <tr>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
      </tr>
    );
  }
  return (
    <tr className="font-weight-bold">
      <td>{translate.Total}</td>
      <td>{sumList(response.list, (x) => x.cant)}</td>
      <td>
        {formatCurrency(response.total, CompanyService.getDefaultCurrencyId())}
      </td>
      <td>{response.total !== 0 ? formatNumber(100) + "%" : "-"}</td>
      <td></td>
    </tr>
  );
};

export default ReportClientGroup;
